import { useState, type ReactNode } from 'react';
import { TooltipProvider } from '../ui/tooltip';
import { ErrorBoundary } from './error-boundary';
import { Sidebar } from './sidebar';
import { Header } from './header';
import type {
  AdaptedNavGroup,
  AdaptedNavItem,
  ResolvedBranding,
  RouteTitle,
  SidebarBadge,
} from './types';

interface AppShellProps {
  branding: ResolvedBranding;
  groups: AdaptedNavGroup[];
  head: RouteTitle;
  /** Optional per-nav-item decorator for badges (e.g. tasks count). */
  decorate?: (item: AdaptedNavItem) => SidebarBadge | undefined;
  /** Opt-in collapsible nav groups. Default preserves existing portal behavior. */
  collapsibleNavGroups?: boolean;
  navGroupsInitiallyCollapsed?: boolean;
  navGroupStorageKey?: string;
  /** Opt-in desktop sidebar collapse. Mobile drawer behavior is unchanged. */
  collapsibleSidebar?: boolean;
  sidebarStorageKey?: string;
  /** Optional app-specific search input rendered in the Header. */
  searchSlot?: ReactNode;
  /** Optional app-specific action buttons in the Header. */
  actions?: ReactNode;
  /** Optional app-specific dialogs / floating UI rendered after the main shell. */
  overlays?: ReactNode;
  /** Optional persistent right-side app panel, such as an assistant/context rail. */
  rightSidebar?: ReactNode;
  /** Optional Sidebar footer override. */
  sidebarFooter?: ReactNode;
  children: ReactNode;
}

export function AppShell({
  branding,
  groups,
  head,
  decorate,
  collapsibleNavGroups,
  navGroupsInitiallyCollapsed,
  navGroupStorageKey,
  collapsibleSidebar,
  sidebarStorageKey,
  searchSlot,
  actions,
  overlays,
  rightSidebar,
  sidebarFooter,
  children,
}: AppShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
        <Sidebar
          branding={branding}
          groups={groups}
          mobileOpen={mobileNavOpen}
          onMobileOpenChange={setMobileNavOpen}
          decorate={decorate}
          collapsibleGroups={collapsibleNavGroups}
          navGroupsInitiallyCollapsed={navGroupsInitiallyCollapsed}
          navGroupStorageKey={navGroupStorageKey}
          collapsibleSidebar={collapsibleSidebar}
          sidebarStorageKey={sidebarStorageKey}
          footer={sidebarFooter}
        />
        <div className="flex min-w-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col">
            <Header
              head={head}
              onOpenMobileNav={() => setMobileNavOpen(true)}
              searchSlot={searchSlot}
              actions={actions}
            />
            <main className="flex-1 overflow-y-auto px-3 py-3 sm:px-5 sm:py-4">
              <ErrorBoundary title="This view crashed.">{children}</ErrorBoundary>
            </main>
          </div>
          {rightSidebar && (
            <aside className="hidden min-h-0 flex-shrink-0 border-l border-border bg-card md:flex">
              {rightSidebar}
            </aside>
          )}
        </div>
      </div>
      {overlays}
    </TooltipProvider>
  );
}
